/* eslint-disable import/no-unresolved */
import { Helmet } from 'react-helmet-async';
import heroImage from '../../images/car-inspection-cost-hero.jpg';

export default function CarInspectionCostPage() {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How much does a car inspection cost in Kerala?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Focus Motors charges Rs.4,999 for standard cars and Rs.7,999 for German vehicles (BMW, Mercedes-Benz, Audi) and other premium or luxury cars (Range Rover, Volvo, Jaguar).',
        },
      },
      {
        '@type': 'Question',
        name: 'Why do German and luxury cars cost more to inspect?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'The inspection covers the same 130+ points for every car. German and premium vehicles cost more due to their complex electronics and specialized systems, which require additional time and expertise to inspect thoroughly.',
        },
      },
      {
        '@type': 'Question',
        name: 'Are there any hidden charges?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'No. Our pricing is fully transparent — Rs.4,999 for standard cars and Rs.7,999 for German and premium vehicles, with no hidden fees.',
        },
      },
      {
        '@type': 'Question',
        name: 'Does the inspection cost vary by location within Kerala?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'No, our pricing is the same across all of Kerala, whether in Kochi, Thrissur, Kozhikode, or any other city.',
        },
      },
    ],
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Car Inspection Service',
    provider: { '@type': 'Organization', name: 'Focus Motors', url: 'https://focusmotors.in' },
    areaServed: { '@type': 'State', name: 'Kerala' },
    url: 'https://focusmotors.in/car-inspection-cost-kerala',
    description:
      'Transparent car inspection pricing in Kerala. Rs.4,999 for standard cars and Rs.7,999 for German and premium/luxury vehicles.',
    offers: [
      {
        '@type': 'Offer',
        name: 'Standard Car Inspection',
        price: '4999',
        priceCurrency: 'INR',
        url: 'https://focusmotors.in/inspection-booking',
      },
      {
        '@type': 'Offer',
        name: 'German & Premium Car Inspection',
        price: '7999',
        priceCurrency: 'INR',
        url: 'https://focusmotors.in/inspection-booking',
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>Car Inspection Cost in Kerala | Focus Motors</title>
        <meta
          name="description"
          content="Transparent car inspection pricing in Kerala — Rs.4,999 for standard cars, Rs.7,999 for German and premium vehicles. Same 130+ point checks for all cars."
        />
        <meta
          name="keywords"
          content="car inspection cost Kerala, car inspection price Kerala, car inspection charges Kerala"
        />
        <link rel="canonical" href="https://focusmotors.in/car-inspection-cost-kerala" />
        <meta property="og:title" content="Car Inspection Cost in Kerala | Focus Motors" />
        <meta
          property="og:description"
          content="Transparent car inspection pricing — Rs.4,999 for standard cars, Rs.7,999 for German and premium vehicles."
        />
        <meta property="og:url" content="https://focusmotors.in/car-inspection-cost-kerala" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      <div
        style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px', fontFamily: 'inherit', color: '#ffffff' }}
      >
        <img
          src={heroImage}
          alt="Car inspection cost and pricing in Kerala"
          style={{
            width: '100%',
            height: 'auto',
            maxHeight: '450px',
            objectFit: 'cover',
            borderRadius: '12px',
            marginBottom: '30px',
          }}
        />

        <h1>Car Inspection Cost in Kerala</h1>
        <p>
          Focus Motors offers transparent, straightforward pricing for car inspection services across Kerala — no hidden
          charges, no surprises.
        </p>

        <h2>Our Pricing</h2>

        <div
          style={{ marginBottom: '20px', padding: '20px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}
        >
          <h3>Standard Cars — Rs.4,999</h3>
          <p>
            Applicable for all standard cars — hatchbacks, sedans, and regular SUVs. Includes a complete 130+ point
            inspection.
          </p>
        </div>

        <div
          style={{ marginBottom: '20px', padding: '20px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}
        >
          <h3>German & Premium Cars — Rs.7,999</h3>
          <p>
            Applicable for German vehicles (BMW, Mercedes-Benz, Audi) and other premium or luxury cars (Range Rover,
            Volvo, Jaguar). Same 130+ point inspection — the higher price reflects the additional expertise and time
            needed for these vehicles' complex systems.
          </p>
        </div>

        <h2>Why Pricing Differs by Car Type</h2>
        <p>
          Every inspection covers the same thorough 130+ point check, regardless of the car. German and premium vehicles
          have more complex electronics, advanced safety systems, and specialized components that require extra time and
          expertise to inspect properly — that's the only reason for the price difference, not fewer checks.
        </p>

        <a
          href="https://focusmotors.in/inspection-booking"
          style={{
            display: 'inline-block',
            marginTop: '10px',
            padding: '12px 24px',
            background: '#000',
            color: '#fff',
            textDecoration: 'none',
            borderRadius: '6px',
          }}
        >
          Book an Inspection
        </a>

        <h2>Available Across Kerala</h2>
        <p>
          Our pricing is the same across all of Kerala — Kochi, Thrissur, Kozhikode, Thiruvananthapuram, and more. Our
          inspector comes to the car's location.
        </p>

        <h2>Frequently Asked Questions</h2>

        <h3>How much does a car inspection cost in Kerala?</h3>
        <p>
          Rs.4,999 for standard cars and Rs.7,999 for German vehicles (BMW, Mercedes-Benz, Audi) and other premium or
          luxury cars (Range Rover, Volvo, Jaguar).
        </p>

        <h3>Why do German and luxury cars cost more to inspect?</h3>
        <p>
          The inspection covers the same 130+ points for every car. German and premium vehicles cost more due to their
          complex electronics and specialized systems.
        </p>

        <h3>Are there any hidden charges?</h3>
        <p>No. Our pricing is fully transparent, with no hidden fees.</p>

        <h3>Does the inspection cost vary by location within Kerala?</h3>
        <p>No, our pricing is the same across all of Kerala.</p>
      </div>
    </>
  );
}
